import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reusable hook for managing list page filter state
 * 
 * @param {Object} config
 * @param {boolean} config.enableSearch - Enable search filter
 * @param {boolean} config.enableTags - Enable tag filtering
 * @param {boolean} config.enableSort - Enable sorting
 * @param {boolean} config.enableStatus - Enable status filter (for ProjectsPage)
 * @param {Array} config.defaultTags - Default selected tags
 * @param {string} config.defaultSort - Default sort value
 * @param {string} config.defaultStatus - Default status value
 * @param {Array} config.sortOptions - Array of { value, label } sort options
 * @param {Array} config.statusOptions - Array of { value, label } status options
 * @param {Function} config.onFiltersChange - Callback when filters change
 * @returns {Object} Filter state and handlers
 */
export const useListPageFilters = ({
  enableSearch = true,
  enableTags = true,
  enableSort = true,
  enableStatus = false,
  defaultTags = [],
  defaultSort = 'popularity',
  defaultStatus = 'active',
  sortOptions = [],
  statusOptions = [],
  onFiltersChange,
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTagsKey = useMemo(() => (defaultTags || []).join(','), [defaultTags]);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');

  // Tag state
  const [selectedTags, setSelectedTags] = useState(() => {
    if (!enableTags) return [];
    const urlTags = searchParams.get('tags');
    if (urlTags) {
      return urlTags.split(',').map((tag) => tag.trim()).filter(Boolean);
    }
    return defaultTags;
  });

  // Sort state
  const [sortBy, setSortBy] = useState(() => {
    const urlSort = searchParams.get('sort');
    return urlSort || defaultSort;
  });

  // Status state (for ProjectsPage)
  const [statusFilter, setStatusFilter] = useState(() => {
    if (!enableStatus) return null;
    const urlTab = searchParams.get('tab');
    return urlTab === 'history' ? 'history' : defaultStatus;
  });

  // Map viewport filter state (synced from ListMapView)
  const [mapViewportBbox, setMapViewportBbox] = useState(null);
  const [mapViewportCenter, setMapViewportCenter] = useState(null);
  const [mapViewportRadius, setMapViewportRadius] = useState(100);
  const [mapUserInteracted, setMapUserInteracted] = useState(false);
  const [mapSyncEnabled, setMapSyncEnabled] = useState(false);

  // Update status filter and URL
  const updateStatusFilter = useCallback(
    (newStatus) => {
      setStatusFilter(newStatus);
      const newSearchParams = new URLSearchParams(searchParams);
      if (newStatus === 'history') {
        newSearchParams.set('tab', 'history');
      } else {
        newSearchParams.delete('tab');
      }
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (!enableTags) return;

    const urlTags = searchParams.get('tags');
    const currentTags = selectedTags.join(',');
    const fallbackTags = defaultTagsKey;

    if (urlTags === currentTags) return;
    if (!urlTags && !currentTags && !fallbackTags) return;

    const nextSearchParams = new URLSearchParams(searchParams);

    if (currentTags && currentTags !== fallbackTags) {
      nextSearchParams.set('tags', currentTags);
    } else {
      nextSearchParams.delete('tags');
    }

    setSearchParams(nextSearchParams, { replace: true });
  }, [
    defaultTagsKey,
    enableTags,
    searchParams,
    selectedTags,
    setSearchParams,
  ]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    if (enableSearch) {
      setSearchTerm('');
      setMobileSearchTerm('');
    }
    if (enableTags) {
      setSelectedTags([]);
    }
    if (enableStatus) {
      updateStatusFilter(defaultStatus);
    }
    if (enableSort) {
      setSortBy(defaultSort);
    }
  }, [
    enableSearch,
    enableTags,
    enableStatus,
    enableSort,
    defaultStatus,
    defaultSort,
    updateStatusFilter,
  ]);

  // Handle search submission
  const handleSearch = useCallback(() => {
    setSearchTerm(mobileSearchTerm);
  }, [mobileSearchTerm]);

  // Build filter summary for mobile UI
  const filterSummary = useMemo(() => {
    const parts = [];
    
    if (enableStatus && statusFilter && statusFilter !== defaultStatus) {
      const statusLabel = statusOptions.find((opt) => opt.value === statusFilter)?.label || statusFilter;
      parts.push(statusLabel);
    }
    
    if (enableTags && selectedTags.length > 0) {
      // This would need tag labels from tagsData - simplified for now
      parts.push(`${selectedTags.length} tag${selectedTags.length !== 1 ? 's' : ''}`);
    }
    
    if (enableSort && sortBy && sortBy !== defaultSort) {
      const sortLabel = sortOptions.find((opt) => opt.value === sortBy)?.label || sortBy;
      parts.push(sortLabel);
    }
    
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [
    enableStatus,
    enableTags,
    enableSort,
    statusFilter,
    selectedTags,
    sortBy,
    defaultStatus,
    defaultSort,
    statusOptions,
    sortOptions,
  ]);

  const mapAreaFilterActive = useMemo(() => {
    if (
      !mapViewportCenter ||
      typeof mapViewportCenter.lat !== 'number' ||
      typeof mapViewportCenter.lng !== 'number'
    ) {
      return false;
    }
    return Boolean(mapUserInteracted && mapSyncEnabled);
  }, [mapViewportCenter, mapUserInteracted, mapSyncEnabled]);

  // Build filters object for useListPageData
  const filters = useMemo(
    () => ({
      searchTerm: enableSearch ? searchTerm : undefined,
      selectedTags: enableTags ? selectedTags : undefined,
      sortBy: enableSort ? sortBy : undefined,
      statusFilter: enableStatus ? statusFilter : undefined,
      mapViewportBbox: mapAreaFilterActive ? mapViewportBbox : undefined,
      mapViewportCenter: mapAreaFilterActive ? mapViewportCenter : undefined,
      mapViewportRadius: mapAreaFilterActive ? mapViewportRadius : undefined,
      mapUserInteracted: mapAreaFilterActive ? mapUserInteracted : undefined,
      mapSyncEnabled: mapAreaFilterActive ? mapSyncEnabled : undefined,
    }),
    [
      enableSearch,
      searchTerm,
      enableTags,
      selectedTags,
      enableSort,
      sortBy,
      enableStatus,
      statusFilter,
      mapAreaFilterActive,
      mapViewportBbox,
      mapViewportCenter,
      mapViewportRadius,
      mapUserInteracted,
      mapSyncEnabled,
    ]
  );

  // Notify parent of filter changes
  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(filters);
    }
  }, [filters, onFiltersChange]);

  return {
    // State
    searchTerm,
    mobileSearchTerm,
    selectedTags,
    sortBy,
    statusFilter,
    mapViewportBbox,
    mapViewportCenter,
    mapViewportRadius,
    mapUserInteracted,
    mapSyncEnabled,

    // Setters
    setSearchTerm,
    setMobileSearchTerm,
    setSelectedTags,
    setSortBy,
    setStatusFilter: updateStatusFilter,
    setMapViewportBbox,
    setMapViewportCenter,
    setMapViewportRadius,
    setMapUserInteracted,
    setMapSyncEnabled,

    // Handlers
    handleSearch,
    clearAllFilters,

    // Computed
    filterSummary,
    filters,
  };
};

